/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useEffect, useMemo, useState } from "react";
import { IconButton, Typography, Select, MenuItem, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppSelector } from "../../../framework/customStore";
import { useTranslation } from "react-i18next";
import DynamicForm from "../../orderTypes/dynamicForm";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import { ConfirmationDialogResult } from "../../../models/common/enums";
import { AppUtils } from "../../../models/common/appUtils";

interface SidebarOrderTypeProps {
    transportOrder: TransportOrderListItemDto;
    onClose: () => void;
    onSave: (values: { field: string; value: any; errors?: any }[]) => void;
    autoFocus: boolean;
}

interface NormalizedSchema {
    type: string;
    [key: string]: any;
}

export const SidebarOrderType = ({ transportOrder, onClose, onSave, autoFocus }: SidebarOrderTypeProps) => {
    const { t } = useTranslation();
    const orderTypes = useAppSelector((state) => state.orderTypes.orderTypes);
    const [selectedOrderTypeId, setSelectedOrderTypeId] = useState(transportOrder?.orderTypeId || "");
    const [formState, setFormState] = useState({
        schema: JSON.parse(transportOrder.formData || "{}"),
        formValues: JSON.parse(transportOrder.formValues || "{}"),
        errors: null,
    });

    const normalizeSchema = (schema: any): NormalizedSchema => {
        try {
            return typeof schema === "string"
                ? (JSON.parse(schema) as NormalizedSchema)
                : (schema as NormalizedSchema) || { type: "object" };
        } catch {
            return { type: "object" };
        }
    };

    const selectedType = useMemo(
        () =>
            orderTypes?.find(
                (type) => type.orderTypeId === selectedOrderTypeId
            ),
        [selectedOrderTypeId, orderTypes]
    );

    const handleChange = async(e) => {
        const newOrderTypeId: string = e.target.value;

        if (newOrderTypeId === selectedOrderTypeId) return;

        if (selectedOrderTypeId) {
            const confirmed = await AppUtils.showConfirmationDialog(
                t("transport.orderAction.changeOrderType"),
                false
            );

            if (confirmed !== ConfirmationDialogResult.Yes) return;
        }
        setSelectedOrderTypeId(newOrderTypeId);
        resetFormState();
    };

    const resetFormState = () => {
        setFormState({
            schema: {},
            formValues: {},
            errors: null,
        });
    };

    useEffect(() => {
        if (selectedType) {
            if (
                selectedType.formSchema?.schemaDetails !==
                transportOrder.formData
            ) {
                setFormState((prevState) => ({
                    ...prevState,
                    schema: normalizeSchema(
                        selectedType.formSchema?.schemaDetails || "{}"
                    ),
                }));
            }
        }
    }, [selectedType]);

    useEffect(() => {
        if (
            !!selectedOrderTypeId &&
            selectedOrderTypeId !== transportOrder.orderTypeId
        ) {
            onSave([{ field: "orderTypeId", value: selectedOrderTypeId }]);
        }
    }, [selectedOrderTypeId]);

    useEffect(() => {
        onSave([
            {
                field: "formData",
                value: JSON.stringify(formState.schema),
            },
            {
                field: "formValues",
                value: JSON.stringify({
                    ...formState.formValues,
                    orderTypeId: selectedOrderTypeId,
                }),
                errors: formState.errors,
            },
        ]);
    }, [formState]);

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography variant="h4">
                    {t("transport.order.orderType")}
                </Typography>
                <IconButton className="close-panel-button" size="small" onClick={onClose}>
                    <CloseIcon color="primary" />
                </IconButton>
            </Stack>
            <Grid2>
                <Typography>{t("transport.order.selectOrderType")}</Typography>
                <Select
                    id="orderType"
                    value={selectedOrderTypeId}
                    onChange={(e) => void handleChange(e)}
                    displayEmpty
                    fullWidth
                    size="small"
                    autoFocus={autoFocus}
                >
                    {orderTypes.map((type) => (
                        <MenuItem
                            key={type.orderTypeId}
                            value={type.orderTypeId}
                        >
                            {type.title}
                        </MenuItem>
                    ))}
                </Select>
            </Grid2>
            <DynamicForm
                schema={formState.schema}
                formValues={formState.formValues}
                uischema={{}}
                onChange={(newFormData) =>
                    setFormState((prev) => ({
                        ...prev,
                        formValues: newFormData,
                        errors: null,
                    }))
                }
                onError={(errors) =>
                    setFormState((prev) => ({ ...prev, errors }))
                }
                validateOnChange
            />
        </Stack>
    );
};
